/**
 * Committed search field - input with inline clear (×) and Search actions.
 * Search runs only on button click or Enter; the submit button enables once the
 * draft contains non-whitespace text.
 *
 * @example
 * ```tsx
 * const search = useCommittedSearch();
 *
 * <CommittedSearchField
 *   value={search.draft}
 *   committed={search.committed}
 *   placeholder="Search classes..."
 *   onChange={(e) => search.setDraft(e.target.value)}
 *   onKeyDown={search.onKeyDown}
 *   onCommit={search.commit}
 *   onClear={search.clear}
 *   hasUnapplied={search.hasUnapplied}
 * />
 * ```
 */

"use client";

import { SearchIcon } from "@chakra-ui/icons";
import { Flex, Input } from "@chakra-ui/react";
import { HiOutlineXMark } from "react-icons/hi2";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface CommittedSearchFieldProps {
  value: string;
  committed?: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onCommit: () => void;
  onClear: () => void;
  hasUnapplied?: boolean;
  /** Attached to the text field (Playwright selector target). */
  "data-testid"?: string;
  minW?: string | number;
  maxW?: string | number;
  w?: string | number;
}

export function CommittedSearchField({
  value,
  committed = "",
  placeholder = "Search...",
  onChange,
  onKeyDown,
  onCommit,
  onClear,
  hasUnapplied = false,
  "data-testid": dataTestId,
  minW = "240px",
  maxW,
  w = "100%",
}: CommittedSearchFieldProps) {
  const theme = useThemeTokens();

  const canSearch = value.trim().length > 0;
  const showClear =
    value.length > 0 || committed.trim().length > 0;
  const searchVariant =
    canSearch && hasUnapplied ? "primary" : "outline";

  return (
    <Flex
      h="44px"
      w={w}
      minW={minW}
      maxW={maxW}
      align="center"
      gap={2}
      pl={3}
      pr={1.5}
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      borderRadius="8px"
      bg="transparent"
      transition="all 0.18s ease-out"
      _focusWithin={{
        borderColor: theme.accent.primaryBorder,
        boxShadow: `0 0 0 1px ${theme.accent.primaryBorder}`,
      }}
    >
      <SearchIcon
        color={theme.text.secondary}
        w="16px"
        h="16px"
        flexShrink={0}
        aria-hidden
      />

      <Input
        flex={1}
        minW={0}
        h="100%"
        variant="unstyled"
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        data-testid={dataTestId}
        placeholder={placeholder}
        fontSize="sm"
        fontWeight="500"
        color={theme.text.primary}
        _placeholder={{ color: theme.text.secondary, fontSize: "14px" }}
      />

      <Flex align="center" gap={0.5} flexShrink={0} w="108px" justify="flex-end">
        <IconButton
          aria-label="Clear search"
          icon={<HiOutlineXMark size={16} />}
          size="sm"
          variant="ghost"
          minW="32px"
          w="32px"
          h="32px"
          visibility={showClear ? "visible" : "hidden"}
          pointerEvents={showClear ? "auto" : "none"}
          tabIndex={showClear ? 0 : -1}
          onClick={onClear}
          data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
        />

        <Button
          h="32px"
          minW="72px"
          px={3}
          fontSize="xs"
          fontWeight="600"
          variant={searchVariant}
          onClick={onCommit}
          isDisabled={!canSearch}
          aria-label="Run search"
          data-testid={dataTestId ? `${dataTestId}-submit` : undefined}
        >
          Search
        </Button>
      </Flex>
    </Flex>
  );
}
